import React from 'react';
import { Paper, Box, Typography, Stack, useTheme } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Tooltip,
    Filler,
    ChartData,
    ChartOptions,
    Chart,
    ScriptableContext,
} from 'chart.js';
import { colors } from '@watter/shared-components';
import { useTranslation } from 'react-i18next';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Filler);

const RED = '#FF5A4D';
const BLUE = '#2FABE1';
const LIGHT_BLUE = '#B3E3F3';
const LIGHT_RED = '#FCDADA';

export const ReactChart: React.FC<{ chartData: { labels: string[]; values: number[] } }> = ({ chartData }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const tickColor = theme.palette.mode === 'dark' ? colors.white : colors.blackColor;
    const barColor = theme.palette.mode === 'dark' ? '#B1B1B1' : colors.info;
    const data: ChartData<'line'> = {
        labels: chartData?.labels,
        datasets: [
            {
                label: 'Temperature',
                data: chartData?.values,
                fill: true,
                backgroundColor: 'rgba(0,0,0,0)',
                borderColor: 'rgba(0,0,0,0)',
                borderWidth: 3,
                pointBackgroundColor: (ctx: ScriptableContext<'line'>) => {
                    const v = ctx.raw as number;
                    return v >= 120 && v <= 140 ? LIGHT_BLUE : LIGHT_RED;
                },
                pointBorderColor: (ctx: ScriptableContext<'line'>) => {
                    const v = ctx.raw as number;
                    return v >= 120 && v <= 140 ? BLUE : RED;
                },
                pointRadius: 7,
                pointHoverRadius: 10,
                pointBorderWidth: 1,
                tension: 0.3,
                cubicInterpolationMode: 'default',
                spanGaps: false,
                pointHoverBackgroundColor: (ctx: ScriptableContext<'line'>) => {
                    const v = ctx.raw as number;
                    return v >= 120 && v <= 140 ? BLUE : RED;
                },
                pointHoverBorderColor: '#fff',
                clip: false,
            },
        ],
    };
    const temperatureArray = Array.isArray(chartData?.values) ? chartData.values : [0];
    const minValue = Math.min(...temperatureArray) - 5;
    const maxValue = Math.max(...temperatureArray) + 5;

    const options: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 35,
            },
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: true,
                backgroundColor: (context: any) => {
                    const value = context.tooltip?.dataPoints?.[0]?.parsed?.y;
                    if (value >= 120 && value <= 140) {
                        return LIGHT_BLUE;
                    }
                    return LIGHT_RED;
                },
                titleColor: tickColor,
                displayColors: false,
                yAlign: 'bottom',
                mode: 'index',
                intersect: true,
                callbacks: {
                    title: () => '',
                    label: (context: any) => `${context.parsed.y}°F`,
                    labelTextColor(context) {
                        const value = context.raw;
                        const v = value as number;
                        if (v >= 120 && v <= 140) {
                            return BLUE;
                        }
                        return RED;
                    },
                },
                padding: 8,
                caretSize: 0,
                caretPadding: 20,
                cornerRadius: 4,
                bodyFont: { size: 12, family: 'good_sansregular' },
            },
        },
        hover: {
            intersect: true,
            mode: 'index',
        },
        scales: {
            x: {
                grid: {
                    display: true,
                    color: barColor, // Very light white color for subtle lines
                    lineWidth: 1,
                    drawTicks: false,
                    z: 0, // Put grid lines behind the curve
                },
                ticks: {
                    color: tickColor,
                    font: { size: 10, family: 'good_sansregular' },
                    padding: 8,
                },
                border: {
                    display: false,
                },
            },
            y: {
                display: false,
                min: minValue, // fix Y-axis range explicitly
                max: maxValue,
                grid: {
                    display: false,
                    lineWidth: 0,
                },
                ticks: { display: false },
                border: {
                    display: false,
                },
            },
        },
        elements: {
            point: {
                hoverBackgroundColor: '#fff',
                hoverBorderWidth: 2.5,
            },
            line: {
                tension: 0.4,
            },
        },
    };

    const getYForValue = (value: number, yScale: any) => yScale.getPixelForValue(value);

    const segmentColorPlugin = {
        id: 'segmentColorPlugin',
        beforeDatasetsDraw: (chart: Chart<'line'>) => {
            const { ctx } = chart;
            const meta = chart.getDatasetMeta(0);
            const points = meta.data;
            const yScale = chart.scales.y;
            ctx.save();
            const thresholdMin = 120;
            const thresholdMax = 140;
            const inThreshold = (v: number) => v >= thresholdMin && v <= thresholdMax;

            for (let i = 0; i < points.length - 1; i += 1) {
                const v1 = data.datasets[0].data[i] as number;
                const x1 = points[i].x;
                const y1 = points[i].y;
                const x2 = points[i + 1].x;
                const y2 = points[i + 1].y;
                const yBase = getYForValue(yScale.min, yScale);

                // Use your threshold logic for color
                const color = inThreshold(v1) ? BLUE : RED;

                // Create a vertical gradient for the fill
                const chartTop = Math.min(y1, y2); // or use chart.chartArea.top for full chart area
                const chartBottom = yBase;
                const grad = ctx.createLinearGradient(0, chartTop, 0, chartBottom);
                grad.addColorStop(0, `${color}E6`); // 0.9 opacity in hex (E6)
                grad.addColorStop(1, `${color}00`); // 0 opacity in hex (00)

                ctx.beginPath();
                ctx.moveTo(x1, y1);
                ctx.bezierCurveTo(
                    x1 + (x2 - x1) * 0.4,
                    y1 + (y2 - y1) * 0.1,
                    x1 + (x2 - x1) * 0.6,
                    y2 - (y2 - y1) * 0.1,
                    x2,
                    y2,
                );
                ctx.lineTo(x2, yBase);
                ctx.lineTo(x1, yBase);
                ctx.closePath();
                ctx.fillStyle = grad;
                ctx.fill();

                // Draw the line segment
                ctx.beginPath();
                ctx.strokeStyle = color;
                ctx.lineWidth = 3;
                ctx.moveTo(x1, y1);
                ctx.bezierCurveTo(
                    x1 + (x2 - x1) * 0.4,
                    y1 + (y2 - y1) * 0.1,
                    x1 + (x2 - x1) * 0.6,
                    y2 - (y2 - y1) * 0.1,
                    x2,
                    y2,
                );
                ctx.stroke();
            }
            ctx.restore();
        },
    };
    const noDataPlugin = {
        id: 'noDataPlugin',
        afterDraw: (chart: any) => {
            const { pdata } = chart.data.datasets[0];
            if (!pdata || pdata.length === 0) {
                const { ctx, chartArea } = chart;
                ctx.save();
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.font = 'bold 18px sans-serif';
                ctx.fillStyle = '#888';
                ctx.fillText(
                    chart.data.datasets[0].data && chart.data.datasets[0].data.length === 0 ? 'No Data Available' : '',
                    (chartArea.left + chartArea.right) / 2,
                    (chartArea.top + chartArea.bottom) / 2,
                );
                ctx.restore();
            }
        },
    };
    return (
        <Paper
            elevation={0}
            className="cstGraph"
            sx={{
                width: '100%',
                background: 'transparent',
            }}
        >
            <Stack alignItems="start">
                <Typography variant="h5" fontWeight={700}>
                    {t('watter_temperature_avg_title')}
                </Typography>
                <Typography variant="subtitle1" fontWeight={400}>
                    {t('last_seven_days')}
                </Typography>
            </Stack>
            <Box width="100%" height={180}>
                <Line data={data} options={options} plugins={[segmentColorPlugin, noDataPlugin]} />
            </Box>
        </Paper>
    );
};
